import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import "./KPICard.css";

/**
 * Format a KPI value for display.
 *
 * Amounts render with 3 decimals (OMR is a 3-decimal currency), whole numbers
 * render plain, and null/undefined renders as an em dash rather than "0" so an
 * absent figure is never mistaken for a real zero.
 */
export function formatKpiValue(value, { unit, allowNegative } = {}) {
  if (value === null || value === undefined || value === "") {
    return { display: "—", isNegative: false };
  }

  const num = Number(value);
  if (Number.isNaN(num)) {
    return { display: String(value), isNegative: false };
  }

  const isNegative = allowNegative && num < 0;
  const abs = Math.abs(num);
  const formatted =
    unit === "%"
      ? abs.toLocaleString(undefined, { maximumFractionDigits: 2 })
      : Number.isInteger(abs)
        ? abs.toLocaleString()
        : abs.toLocaleString(undefined, {
            minimumFractionDigits: 3,
            maximumFractionDigits: 3,
          });

  return {
    display: isNegative ? `-${formatted}` : formatted,
    isNegative,
  };
}

/**
 * KPIGrid — responsive wrapper for a strip of KPI tiles.
 *
 *   <KPIGrid>
 *     {cards.map((c) => <KPICard key={c.key} {...c} />)}
 *   </KPIGrid>
 */
export function KPIGrid({ children, minWidth }) {
  const style = minWidth
    ? { gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}px, 1fr))` }
    : undefined;
  return (
    <div className="kpi-grid" style={style}>
      {children}
    </div>
  );
}

/**
 * KPICard — metric tile shared by Dashboard, Payroll, Payouts and Accounts.
 *
 * Shows label + value (+ optional OMR / % unit). Negative values render red when
 * `allowNegative` is set. `caveat` adds an info marker explaining why a figure
 * may be incomplete, so a known data gap does not get read as a bug.
 * `tone` picks a colour variant ("positive" | "negative" | "dark").
 */
export default function KPICard({
  label,
  value = null,
  unit = null,
  allowNegative = false,
  caveat = null,
  tone = null,
  onClick,
}) {
  const { display, isNegative } = formatKpiValue(value, { unit, allowNegative });

  return (
    <div
      className={`dash-kpi-card${tone ? ` tone-${tone}` : ""}`}
      onClick={onClick}
      style={onClick ? { cursor: "pointer" } : undefined}
    >
      <div className="dash-kpi-label">
        {label}
        {caveat && (
          <Tooltip title={caveat}>
            <InfoCircleOutlined className="dash-kpi-caveat" />
          </Tooltip>
        )}
      </div>
      <div className="dash-kpi-value-row">
        <span className={`dash-kpi-value${isNegative ? " neg" : ""}`}>
          {display}
        </span>
        {unit && unit !== "%" && <span className="dash-kpi-unit">{unit}</span>}
        {unit === "%" && display !== "—" && <span className="dash-kpi-unit">%</span>}
      </div>
    </div>
  );
}
